/**
 * Centralized permission system for the admin portal.
 *
 * All admin-level permission checks route through `hasPermission()`.
 * Office staff permissions are delegatable — principals/VPs can grant
 * specific capabilities to individual office staff members.
 */

import type { OrgRole, SchoolAssociation } from '../types';

// ─── Delegatable permission keys ──────────────────────────────────────────────

export const DELEGATABLE_PERMISSIONS = [
  'manage_branding',
  'manage_teachers',
  'manage_students',
  'manage_attendance_excused',
  'manage_reporting',
  'manage_report_cards',
  'manage_sis',
  'manage_notifications',
  'manage_timetable',
  'manage_emails',
  'manage_graduation',
  'manage_guidance',
  'manage_templates',
] as const;

export type DelegatablePermission = typeof DELEGATABLE_PERMISSIONS[number];

/** Human-readable metadata for each delegatable permission */
export const PERMISSION_META: Record<DelegatablePermission, { label: string; description: string }> = {
  manage_branding:             { label: 'School Branding',    description: 'Can configure school colors and logo' },
  manage_teachers:             { label: 'Teacher Management', description: 'Can invite/remove teachers and approve join requests' },
  manage_students:             { label: 'Student Directory',  description: 'Can add, edit, import students and manage records' },
  manage_attendance_excused:   { label: 'Excused Attendance', description: 'Can mark attendance as Excused' },
  manage_reporting:            { label: 'Reporting Periods',  description: 'Can create reporting periods, set deadlines, configure templates' },
  manage_report_cards:         { label: 'Report Cards',       description: 'Can view teacher submissions, generate/print report cards' },
  manage_sis:                  { label: 'SIS & Rostering',    description: 'Can trigger .arc imports and manage SIS configuration' },
  manage_notifications:        { label: 'Notifications',      description: 'Can send school-wide notifications' },
  manage_timetable:            { label: 'Timetable',          description: 'Can build course catalog, assign teachers, and generate timetables' },
  manage_emails:               { label: 'Email Tools',        description: 'Can compose and send school emails, manage email templates' },
  manage_graduation:           { label: 'Graduation',          description: 'Can view the school-wide graduation dashboard and per-student credit records.' },
  manage_guidance:             { label: 'Guidance',            description: 'Guidance-counsellor access: the graduation dashboard plus confidential student case notes, shared only among guidance staff and hidden from regular teachers.' },
  manage_templates:            { label: 'Shared Templates',    description: 'Can publish and remove class templates in the school\u2019s shared library, which every teacher here can build classes from.' },
};

/** Permissions granted by default to newly invited office staff */
export const DEFAULT_OFFICE_STAFF_PERMISSIONS: DelegatablePermission[] = [
  'manage_attendance_excused',
];

// ─── Admin portal access ──────────────────────────────────────────────────────

/** School-association roles that can open the admin portal. */
export const SCHOOL_ADMIN_ROLES = ['principal', 'vp', 'office_staff'] as const;

/**
 * Can this user open /admin?
 *
 * `userProfile.role` is platform-wide, so a principal/VP/office_staff still reads
 * as 'teacher' there — their admin role only ever appears on a school association.
 * Board admins qualify outright and typically have no association at all.
 *
 * Centralized because the route guard, the post-login redirect and the sidebar all
 * need the same answer, and drifting copies previously routed a VP to the wrong app.
 */
export function hasAdminPortalAccess(
  userRole: string | undefined,
  associations: ReadonlyArray<{ role: string }> = [],
): boolean {
  // A student never reaches the admin portal, whatever their associations claim.
  // Students shouldn't carry one at all; this just refuses to depend on that.
  if (userRole === 'student') return false;
  if (userRole === 'board_admin') return true;
  // A district admin's whole job is the admin portal; they typically hold no
  // school association at all, exactly like the owner.
  if (userRole === 'district_admin') return true;
  return associations.some(a => (SCHOOL_ADMIN_ROLES as readonly string[]).includes(a.role));
}

// ─── Admin permission keys (non-delegatable, role-gated) ──────────────────────

export type AdminPermission =
  | DelegatablePermission
  | 'invite_principal'       // only board_admin
  | 'create_school'          // only board_admin
  | 'manage_billing'         // only board_admin
  | 'view_all_schools';      // only board_admin

// ─── Permission check ─────────────────────────────────────────────────────────

export interface PermissionContext {
  /** The user's top-level Firestore role. `board_admin` is the PLATFORM OWNER
   *  (displayed as "Owner"); `district_admin` is a customer who runs one board. */
  userRole: 'teacher' | 'student' | 'board_admin' | 'district_admin';
  /** The user's role within the current school context (from SchoolAssociation) */
  schoolRole: OrgRole | null;
  /** Delegated permissions array (only populated for office_staff from their staff doc) */
  delegatedPermissions: DelegatablePermission[];
  /** True when the current school is enterprise-managed (school.enterpriseDomain set).
   *  Its branding is owner-controlled, so its principals/VPs do NOT get manage_branding
   *  (the member-facing org logo is owner-only regardless; this also withholds the
   *  cosmetic school logo). board_admin (the platform owner) still keeps branding. */
  isEnterpriseManaged?: boolean;
}

/**
 * Central permission check. Use this everywhere instead of direct role comparisons.
 *
 * - board_admin (the platform OWNER): all permissions, platform-wide
 * - district_admin: everything for schools in THEIR board, except platform-wide
 *   billing and cross-board visibility
 * - principal: all permissions
 * - vp: all permissions EXCEPT invite_principal
 * - office_staff: only explicitly delegated permissions (+ non-admin defaults)
 * - teacher: no admin permissions
 */
export function hasPermission(ctx: PermissionContext, permission: AdminPermission): boolean {
  const { userRole, schoolRole, delegatedPermissions } = ctx;

  // Board admin (the platform owner): everything.
  if (userRole === 'board_admin') return true;

  // District admin — a CUSTOMER who administers one board of schools.
  //
  // Deliberately not "board_admin with a smaller radius": every existing check
  // that reads `board_admin` grants platform-wide access, so a district admin is
  // a separate role and the caller is responsible for having already confirmed
  // the school belongs to their board (firestore.rules enforces that
  // independently via districtBoardId).
  //
  // They may create schools inside their own board and invite principals — that
  // IS the job. They may not touch platform billing, and view_all_schools means
  // every school on the platform, not every school in their board.
  if (userRole === 'district_admin') {
    return permission !== 'manage_billing' && permission !== 'view_all_schools';
  }

  // Enterprise-managed schools: the admin CAN change the school COLOURS (portal
  // tint) but NOT the logo — the member-facing org logo is owner-controlled. So
  // manage_branding is granted here (colours), and the logo input is locked in the
  // Settings UI + firestore.rules (only branding.logoUrl is immutable for a
  // non-owner on an enterprise-managed school).

  // Board-admin-only permissions
  if (permission === 'create_school' || permission === 'manage_billing' || permission === 'view_all_schools') {
    return false;
  }

  // Principal: everything except board-only permissions (already handled above)
  if (schoolRole === 'principal') return true;

  // VP: everything except inviting principals
  if (schoolRole === 'vp') {
    return permission !== 'invite_principal';
  }

  // Office staff: only delegated permissions
  if (schoolRole === 'office_staff') {
    // invite_principal is never delegatable to office staff
    if (permission === 'invite_principal') return false;
    return delegatedPermissions.includes(permission as DelegatablePermission);
  }

  // Teacher or unknown: no admin permissions
  return false;
}

// ─── Role filtering for invitations ───────────────────────────────────────────

/** Returns the OrgRoles the current user is allowed to invite */
export function getInvitableRoles(ctx: PermissionContext): OrgRole[] {
  const { userRole, schoolRole } = ctx;

  // Board admin: can invite all roles
  if (userRole === 'board_admin') {
    return ['principal', 'vp', 'office_staff', 'teacher'];
  }

  // Principal: can invite all roles
  if (schoolRole === 'principal') {
    return ['principal', 'vp', 'office_staff', 'teacher'];
  }

  // VP: can invite all except principal
  if (schoolRole === 'vp') {
    return ['vp', 'office_staff', 'teacher'];
  }

  // Office staff with manage_teachers: can invite teachers only
  if (schoolRole === 'office_staff' && hasPermission(ctx, 'manage_teachers')) {
    return ['teacher'];
  }

  return [];
}

// ─── Helper to build PermissionContext from app state ──────────────────────────

export function buildPermissionContext(
  userRole: 'teacher' | 'student' | 'board_admin' | 'district_admin',
  currentAssociation: SchoolAssociation | null,
  delegatedPermissions: DelegatablePermission[] = [],
  isEnterpriseManaged = false,
  /**
   * True when the platform OWNER is inspecting a school they don't belong to
   * ("view as school admin"). Drops the board_admin role for this context.
   *
   * Without this the view is actively misleading rather than merely permissive:
   * hasPermission short-circuits board_admin to true for EVERY permission,
   * including the three that are board-only (create_school, manage_billing,
   * view_all_schools — NOT invite_principal, which a principal really does
   * hold; it's the VP who can't). The owner would be shown options no
   * principal has, while the whole point of the mode is answering "what do they
   * see, and what can they do". Downgrading to 'teacher' lets the synthesized
   * schoolRole ('principal') decide — which is exactly what a real principal's
   * context looks like.
   *
   * Client-side rendering only. Firestore rules still see the owner's real
   * credentials, so this narrows what is DISPLAYED, never what is permitted.
   */
  viewingAsSchoolAdmin = false,
): PermissionContext {
  return {
    userRole: viewingAsSchoolAdmin && userRole === 'board_admin' ? 'teacher' : userRole,
    schoolRole: currentAssociation?.role ?? null,
    delegatedPermissions,
    isEnterpriseManaged,
  };
}
